import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ja from 'date-fns/locale/ja';
import { getAuthHeader } from '../utils/auth'; // ★ 1. 認証ヘッダー取得関数をインポート

// react-datepickerで日本語を使えるように設定
registerLocale('ja', ja);

// APIサーバーのURL
const API_URL = "https://salesbreaker.jp";

// --- アイコンコンポーネント ---
const RefreshIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>;
const CpuIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect><rect x="9" y="9" width="6" height="6"></rect><line x1="9" y1="1" x2="9" y2="4"></line><line x1="15" y1="1" x2="15" y2="4"></line><line x1="9" y1="20" x2="9" y2="23"></line><line x1="15" y1="20" x2="15" y2="23"></line><line x1="20" y1="9" x2="23" y2="9"></line><line x1="20" y1="14" x2="23" y2="14"></line><line x1="1" y1="9" x2="4" y2="9"></line><line x1="1" y1="14" x2="4" y2="14"></line></svg>;
const MemoryIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12h10"/><path d="M2 18h10"/><path d="M14 2v4"/><path d="M14 8v4"/><path d="M14 14v4"/><path d="M14 20v2"/><path d="M5 2h14a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/></svg>;
const CheckCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;

// --- UIコンポーネント ---
const StatusBadge = ({ status }) => {
  const statusStyles = {
    Online: "bg-green-100 text-green-800",
    Suspended: "bg-yellow-100 text-yellow-800",
    Offline: "bg-red-100 text-red-800",
  };
  const statusText = {
    Online: "オンライン",
    Suspended: "一時停止中",
    Offline: "オフライン",
  };
  return (
    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
      {statusText[status] || '不明'}
    </span>
  );
};

const MetricCard = ({ icon, title, value, unit }) => (
  <div className="bg-white p-4 rounded-lg shadow">
    <div className="flex items-center">
      <div className="p-2 bg-indigo-100 text-indigo-600 rounded-full mr-4">
        {icon}
      </div>
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900">
          {value}
          <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>
        </p>
      </div>
    </div>
  </div>
);

const ProgressBar = ({ value, colorClass = "bg-blue-500" }) => (
  <div className="w-full bg-gray-200 rounded-full h-2.5">
    <div className={`${colorClass} h-2.5 rounded-full`} style={{ width: `${Math.min(value, 100)}%` }}></div>
  </div>
);

const VpsDetailModal = ({ vps, onClose }) => {
    const [graphData, setGraphData] = useState([]);
    const [isLoadingGraph, setIsLoadingGraph] = useState(true);

    useEffect(() => {
        if (!vps) return;
        setIsLoadingGraph(true);
        fetch(`${API_URL}/api/admin/vps-metrics-history?vpsId=${vps.id}`, { // ★ 2. 認証ヘッダーを追加
            headers: getAuthHeader()
        })
            .then(res => res.json())
            .then(data => {
                if (data.success && data.history) {
                    const sanitizedData = data.history.map(item => ({
                        ...item,
                        cpu: Math.min(item.cpu, 100),
                        memory: Math.min(item.memory, 100)
                    }));
                    setGraphData(sanitizedData);
                } else {
                    console.error("グラフデータの取得に失敗:", data.error);
                    setGraphData([]);
                }
            })
            .catch(err => {
                console.error("グラフデータの通信エラー:", err);
                setGraphData([]);
            })
            .finally(() => setIsLoadingGraph(false));
    }, [vps]);

    if (!vps) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-3 sm:p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">
                <div className="flex justify-between items-center gap-3 p-4 border-b">
                    <h2 className="min-w-0 break-all text-lg sm:text-xl font-bold text-gray-800">VPS詳細: {vps.id}</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full"><CloseIcon/></button>
                </div>
                <div className="p-4 sm:p-6 overflow-y-auto">
                    <h3 className="text-lg font-semibold mb-4">負荷状況グラフ (本日)</h3>
                    {isLoadingGraph ? (
                        <div className="h-80 flex justify-center items-center">読み込み中...</div>
                    ) : (
                        <div style={{ width: '100%', height: 300 }}>
                            <ResponsiveContainer>
                                <LineChart data={graphData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="time" />
                                    <YAxis yAxisId="left" domain={[0, 100]} label={{ value: 'CPU (%)', angle: -90, position: 'insideLeft' }} />
                                    <YAxis yAxisId="right" domain={[0, 100]} orientation="right" label={{ value: 'Memory (%)', angle: -90, position: 'insideRight' }} />
                                    <Tooltip />
                                    <Legend />
                                    <Line yAxisId="left" type="monotone" dataKey="cpu" stroke="#8884d8" name="CPU" dot={false} />
                                    <Line yAxisId="right" type="monotone" dataKey="memory" stroke="#82ca9d" name="メモリ" dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                     <div className="mt-8 pt-6 border-t">
                        <h3 className="text-lg font-semibold mb-4">VPS遠隔操作</h3>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <button className="bg-blue-500 text-white font-bold py-2 px-4 rounded hover:bg-blue-600 disabled:opacity-50" disabled>VPSを再起動</button>
                            <button className="bg-yellow-500 text-white font-bold py-2 px-4 rounded hover:bg-yellow-600 disabled:opacity-50">一時停止</button>
                            <button className="bg-red-500 text-white font-bold py-2 px-4 rounded hover:bg-red-600 disabled:opacity-50">プールから削除</button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">※この機能は現在開発中です。</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- メインコンポーネント ---
function VpsStatusDashboard() {
  const [fleetStatus, setFleetStatus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedVps, setSelectedVps] = useState(null);

  const [activeFilter, setActiveFilter] = useState('today');
  const [dateRange, setDateRange] = useState({ startDate: new Date(), endDate: new Date() });

  const formatDate = (date) => date.toISOString().split('T')[0];

  const handleDateFilterClick = useCallback((filterType) => {
    setActiveFilter(filterType);
    const today = new Date();
    let newStartDate = new Date();
    let newEndDate = new Date();

    if (filterType === 'today') {
      // startDate and endDate are already today
    } else if (filterType === 'this_week') {
      const dayOfWeek = today.getDay();
      newStartDate.setDate(today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)); // Monday start
    } else if (filterType === 'this_month') {
      newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
    } else if (filterType === 'last_year') {
      newStartDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    }
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
          startDate: formatDate(dateRange.startDate),
          endDate: formatDate(dateRange.endDate),
      });
      const response = await fetch(`${API_URL}/api/admin/vps-fleet-status?${params.toString()}`, { // ★ 2. 認証ヘッダーを追加
        headers: getAuthHeader()
      });
      if (!response.ok) throw new Error(`サーバーエラー: ${response.status}`);
      
      const data = await response.json();
      if (data.success) {
        setFleetStatus(data.fleetStatus || []);
        setLastUpdated(new Date());
      } else {
        throw new Error(data.error || "データの取得に失敗しました。");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
  fetchData();
  // setIntervalをコメントアウト。大西さんが見たい時だけ「手動更新」を押す。
  /* const intervalId = setInterval(fetchData, 30000);
  return () => clearInterval(intervalId);
  */
}, [fetchData]);

  const summary = useMemo(() => fleetStatus.reduce((acc, vps) => {
      acc.total++;
      if (vps.status === 'Online') acc.online++;
      if (vps.status === 'Suspended') acc.suspended++;
      acc.totalAttempts += Number(vps.totalAttempts) || 0;
      acc.totalSuccesses += Number(vps.successCount) || 0;
      return acc;
  }, { total: 0, online: 0, suspended: 0, totalAttempts: 0, totalSuccesses: 0 }), [fleetStatus]);

  const overallSuccessRate = summary.totalAttempts > 0 
    ? ((summary.totalSuccesses / summary.totalAttempts) * 100).toFixed(1)
    : '0.0';

  if (error) {
    return <div className="p-8 text-center text-red-600 bg-red-50 rounded-lg">エラー: {error}</div>;
  }

  return (
    <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
      <div className="max-w-screen-xl mx-auto">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">VPS稼働状況</h1>
          <div className="grid w-full grid-cols-1 gap-2 sm:w-auto sm:grid-cols-[auto_auto] sm:items-center">
            <span className="text-sm text-gray-500 sm:text-right">
              {lastUpdated ? `最終更新: ${lastUpdated.toLocaleTimeString('ja-JP')}` : ''}
            </span>
            <button onClick={fetchData} disabled={isLoading} className="flex w-full items-center justify-center gap-2 bg-white text-gray-700 font-semibold py-2 px-4 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 sm:w-auto">
              <RefreshIcon />
              {isLoading ? '更新中...' : '手動更新'}
            </button>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-md mb-8">
            <h3 className="text-md font-semibold mb-3">集計期間</h3>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                <button onClick={() => handleDateFilterClick('today')} className={`text-sm py-1 px-3 rounded-full ${activeFilter === 'today' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}>本日</button>
                <button onClick={() => handleDateFilterClick('this_week')} className={`text-sm py-1 px-3 rounded-full ${activeFilter === 'this_week' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}>今週</button>
                <button onClick={() => handleDateFilterClick('this_month')} className={`text-sm py-1 px-3 rounded-full ${activeFilter === 'this_month' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}>今月</button>
                <button onClick={() => handleDateFilterClick('last_year')} className={`text-sm py-1 px-3 rounded-full ${activeFilter === 'last_year' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}>過去1年</button>
                <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-2 sm:w-auto">
                    <DatePicker
                        selected={dateRange.startDate}
                        onChange={(date) => { setDateRange(prev => ({ ...prev, startDate: date })); setActiveFilter('custom'); }}
                        selectsStart
                        startDate={dateRange.startDate}
                        endDate={dateRange.endDate}
                        dateFormat="yyyy/MM/dd"
                        locale="ja"
                        className="w-full min-w-0 p-2 sm:p-1 border rounded-md text-sm sm:w-32"
                    />
                    <span>〜</span>
                    <DatePicker
                        selected={dateRange.endDate}
                        onChange={(date) => { setDateRange(prev => ({ ...prev, endDate: date })); setActiveFilter('custom'); }}
                        selectsEnd
                        startDate={dateRange.startDate}
                        endDate={dateRange.endDate}
                        minDate={dateRange.startDate}
                        dateFormat="yyyy/MM/dd"
                        locale="ja"
                        className="w-full min-w-0 p-2 sm:p-1 border rounded-md text-sm sm:w-32"
                    />
                </div>
            </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
          <MetricCard icon={<CheckCircleIcon />} title="オンラインVPS" value={`${summary.online} / ${summary.total}`} unit="台" />
          <MetricCard icon={<CpuIcon />} title="総送信数" value={summary.totalAttempts.toLocaleString()} unit="件" />
          <MetricCard icon={<MemoryIcon />} title="総成功数" value={summary.totalSuccesses.toLocaleString()} unit="件" />
          <MetricCard icon={<CheckCircleIcon />} title="全体成功率" value={overallSuccessRate} unit="%" />
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="divide-y divide-gray-100 md:hidden">
            {isLoading && fleetStatus.length === 0 ? (
              <div className="p-8 text-center text-gray-500">データを読み込んでいます...</div>
            ) : fleetStatus.length === 0 ? (
              <div className="p-8 text-center text-gray-500">表示できるVPSがありません</div>
            ) : (
              fleetStatus.map(vps => (
                <div key={`${vps.id}-mobile`} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="break-all font-semibold text-gray-900">{vps.id}</p>
                      <p className="mt-1 text-xs text-gray-500">{vps.lastUpdated ? new Date(vps.lastUpdated).toLocaleTimeString('ja-JP') : 'N/A'}</p>
                    </div>
                    <StatusBadge status={vps.status} />
                  </div>
                  <div className="mt-4 space-y-3">
                    <div>
                      <div className="mb-1 flex justify-between text-xs font-semibold text-gray-500">
                        <span>CPU</span>
                        <span>{vps.cpuUsage}%</span>
                      </div>
                      <ProgressBar value={vps.cpuUsage} colorClass="bg-blue-500" />
                    </div>
                    <div>
                      <div className="mb-1 flex justify-between text-xs font-semibold text-gray-500">
                        <span>メモリ</span>
                        <span>{vps.memoryUsage}%</span>
                      </div>
                      <ProgressBar value={vps.memoryUsage} colorClass="bg-teal-500" />
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-md bg-gray-50 p-3">
                      <p className="text-xs font-semibold text-gray-500">送信数</p>
                      <p className="mt-1 font-bold text-gray-900">{Number(vps.totalAttempts || 0).toLocaleString()}</p>
                    </div>
                    <div className="rounded-md bg-gray-50 p-3">
                      <p className="text-xs font-semibold text-gray-500">成功率</p>
                      <p className="mt-1 font-bold text-gray-900">{vps.successRate}%</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedVps(vps)} className="mt-4 w-full rounded-md border border-blue-200 px-3 py-2 text-sm font-semibold text-blue-700">
                    詳細グラフ
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm text-left text-gray-600">
              <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                <tr>
                  <th className="py-3 px-4">VPS ID</th>
                  <th className="py-3 px-4">ステータス</th>
                  <th className="py-3 px-4">CPU / メモリ</th>
                  <th className="py-3 px-4">送信数</th>
                  <th className="py-3 px-4">成功率</th>
                  <th className="py-3 px-4">最終更新</th>
                  <th className="py-3 px-4">アクション</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading && fleetStatus.length === 0 ? (
                  <tr><td colSpan="7" className="p-8 text-center text-gray-500">データを読み込んでいます...</td></tr>
                ) : (
                  fleetStatus.map(vps => (
                    <tr key={vps.id} className="hover:bg-blue-50">
                      <td className="py-3 px-4 font-medium">{vps.id}</td>
                      <td className="py-3 px-4"><StatusBadge status={vps.status} /></td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1">
                            <ProgressBar value={vps.cpuUsage} colorClass="bg-blue-500" />
                            <ProgressBar value={vps.memoryUsage} colorClass="bg-teal-500" />
                        </div>
                      </td>
                      <td className="py-3 px-4">{vps.totalAttempts.toLocaleString()}</td>
                      <td className="py-3 px-4">{vps.successRate}%</td>
                      <td className="py-3 px-4 text-xs text-gray-500">{vps.lastUpdated ? new Date(vps.lastUpdated).toLocaleTimeString('ja-JP') : 'N/A'}</td>
                      <td className="py-3 px-4">
                          <button onClick={() => setSelectedVps(vps)} className="text-blue-600 hover:underline text-xs font-semibold">
                              詳細グラフ
                          </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <VpsDetailModal vps={selectedVps} onClose={() => setSelectedVps(null)} />
    </div>
  );
}

export default VpsStatusDashboard;
